import React, { useEffect, useState, useMemo } from 'react';
import { createState, useState as useStateHoockState } from '@hookstate/core';
import * as UiActions from 'actions/UiActions';
import cls from 'classnames';
import { isDesktop, isMobile } from 'react-device-detect';

import Button from 'ui/Button';
import Group from './Group';
import Spinner from '../../../ecoui/UISpinner';
import Text from 'ui/Text';

import useScreens, {
    CREATE_GROUP_SLUG,
    getScreenUrl,
    GROUP_LIST_SLUG,
} from '../screens/useScreens';
import useGroupInfiniteList, {
    GROUP_TYPE_EXPERT_GROUP,
    GROUP_TYPE_OWN_GROUP,
    GROUP_TYPE_PUBLIC_GROUP,
} from '../state/useGroupInfiniteList';
import useAuth from 'features/SpilKlub/state/useAuth';

import '../styles/groups.css';

import WelcomeText from './WelcomeText';
import { detectMobile } from '../../../utils/UiHelper';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { authHandler } from '../index';
import LoginWarning from './LoginWarning';
import { checkUserLoggedStatus } from '../../../actions/AuthActions';

const btnExtra = {
    neutral: true,
    margin: '0',
    padding: '15px 20px',
};

const TABS = {
    publicClubs: GROUP_TYPE_PUBLIC_GROUP,
    ownClubs: GROUP_TYPE_OWN_GROUP,
};

const LABELS = {
    publicClubs: 'Alle spilklubber',
    ownClubs: 'Dine spilklubber',
};

const homepageTab = createState('publicClubs');
const Groups = ({ className, uiActions, checkUserLoggedStatus }) => {
    const state = useStateHoockState(homepageTab);
    const groupType = TABS[state.value];
    const {
        data: expertGroupsData,
        hasNextPage: expertGroupsHasNextPage,
        isFetchingNextPage: expertGroupsIsFetchingNextPage,
        fetchNextPage: fetchNextExpertPage,
    } = useGroupInfiniteList({ groupType: GROUP_TYPE_EXPERT_GROUP });

    const {
        data: publicGroupsData,
        hasNextPage: publicGroupsHasNextPage,
        isFetchingNextPage: publicGroupsIsFetchingNextPage,
        fetchNextPage: fetchNextPublicPage,
    } = useGroupInfiniteList({ groupType: GROUP_TYPE_PUBLIC_GROUP });

    const {
        data: ownGroupsData,
        hasNextPage: ownGroupsHasNextPage,
        isFetchingNextPage: ownGroupsIsFetchingNextPage,
        fetchNextPage: fetchNextOwnPage,
    } = useGroupInfiniteList({ groupType: GROUP_TYPE_OWN_GROUP });

    const showLogin = () => {
        authHandler(uiActions, CREATE_GROUP_SLUG, {
            redirectBackPath: getScreenUrl(GROUP_LIST_SLUG),
        });
    };

    const mergedPublicAndExpertData = useMemo(
        () => {
            if (publicGroupsData?.pages && expertGroupsData?.pages) {
                return {
                    pages: [
                        ...expertGroupsData.pages,
                        ...publicGroupsData.pages,
                    ],
                };
            }
            return { pages: [] };
        },
        [publicGroupsData, expertGroupsData]
    );

    checkUserLoggedStatus().then((response) => {});

    const { loggedIn } = useAuth();
    const { setScreen } = useScreens();
    const [amountOfGroups, setAmountOfGroups] = useState(0);

    const btnStyles = { marginRight: isDesktop ? '20px' : 0 };

    const showGroupForm = () => {
        setScreen(CREATE_GROUP_SLUG);
    };

    useEffect(
        () => {
            if (
                publicGroupsData?.pages &&
                expertGroupsData?.pages &&
                state.value !== 'ownClubs'
            ) {
                let numbers =
                    calculateGroupCount(publicGroupsData) +
                    calculateGroupCount(expertGroupsData);
                setAmountOfGroups(numbers);
            } else if (ownGroupsData?.pages) {
                let numbers = calculateGroupCount(ownGroupsData);
                setAmountOfGroups(numbers);
            }
        },
        [publicGroupsData, expertGroupsData, state.value, loggedIn]
    );

    const calculateGroupCount = (groupsData) => {
        let count = 0;
        groupsData.pages.forEach((page) => {
            count += page.groups.length;
        });
        return count;
    };

    return (
        <div className="sk-wrapper">
            <div className="sk-sidebar">
                <WelcomeText />
                {!detectMobile() && (
                    <Text
                        size={'26'}
                        margin={'30px 0 32px'}
                        padding={'0'}
                        bold
                        color={'#333'}
                    >
                        Filtre
                    </Text>
                )}
                <div
                    className={cls('flex', 'sk-tab-btn', className, {
                        'space-between': isMobile,
                        'flex-start': isDesktop,
                    })}
                >
                    <Button
                        {...btnExtra}
                        active={state.value === 'publicClubs'}
                        onClick={() => state.set('publicClubs')}
                        style={btnStyles}
                    >
                        {LABELS.publicClubs}
                    </Button>
                    <Button
                        {...btnExtra}
                        active={state.value === 'ownClubs'}
                        onClick={() => state.set('ownClubs')}
                    >
                        {LABELS.ownClubs}
                    </Button>
                </div>
            </div>
            <div className="sk-content">
                {!detectMobile() && amountOfGroups !== 0 ? (
                    <Text
                        align={'left'}
                        margin={'0'}
                        padding={'0'}
                        size={'26'}
                        bold
                        color={'#333'}
                    >
                        {`Viser nu ${amountOfGroups} klubber`}
                    </Text>
                ) : null}

                {state.value === 'ownClubs' &&
                    !loggedIn && <LoginWarning loggedIn={loggedIn} />}
                {state.value === 'ownClubs' &&
                    loggedIn &&
                    (ownGroupsData?.pages[0]?.groups.length === 0 ? (
                        <>
                            <LoginWarning loggedIn={loggedIn} />
                        </>
                    ) : (
                        renderGroups(ownGroupsData, loggedIn, 'ownClubs')
                    ))}
                {state.value === 'publicClubs' &&
                    (mergedPublicAndExpertData.pages.length > 0 ? (
                        renderGroups(
                            mergedPublicAndExpertData,
                            loggedIn,
                            'publicClubs'
                        )
                    ) : (
                        <Spinner />
                    ))}
                {loggedIn && (
                    <div className={'sk-wrapper-login-btn'}>
                        <Button
                            onClick={showGroupForm}
                            active
                            className="add-group-btn"
                        >
                            {'Tilføj spil til Klub'}
                        </Button>
                    </div>
                )}
                {state.value === 'publicClubs' &&
                    publicGroupsHasNextPage &&
                    !publicGroupsIsFetchingNextPage && (
                        <Button
                            onClick={() => fetchNextPublicPage()}
                            neutral
                            className="sk-show-more-btn fade-in"
                        >
                            Vis flere
                        </Button>
                    )}

                {state.value === 'ownClubs' &&
                    ownGroupsHasNextPage &&
                    !ownGroupsIsFetchingNextPage && (
                        <Button
                            onClick={() => fetchNextOwnPage()}
                            neutral
                            className="sk-show-more-btn fade-in"
                        >
                            Vis flere
                        </Button>
                    )}

                {(publicGroupsIsFetchingNextPage ||
                    expertGroupsIsFetchingNextPage) && (
                    <Spinner className="top-indent-md" />
                )}
            </div>
        </div>
    );
};

const renderGroups = (groupsData, loggedIn, tab) => {
    let groups = [];
    if (groupsData?.pages?.length > 0) {
        groups = groupsData.pages.flatMap((page) => page.groups || []);
    }

    return groups.map((group) => (
        <Group
            key={group.poolTemplateId}
            group={group}
            isLoggedIn={loggedIn}
            expertGroup={group.expertGroup}
        />
    ));
};

function mapDispatchToProps(dispatch) {
    return {
        uiActions: bindActionCreators(UiActions, dispatch),
        checkUserLoggedStatus: () => dispatch(checkUserLoggedStatus()),
    };
}

export default connect(
    null,
    mapDispatchToProps
)(Groups);
